import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
  requireBrandAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireSuperAdmin = false,
  requireBrandAdmin = false 
}) => {
  const { user, profile, loading, canAccessSuperAdmin, canAccessBrandAdmin } = useAuth();

  console.log('🛡️ ProtectedRoute check:', {
    user: user?.email,
    profile,
    loading,
    requireSuperAdmin,
    requireBrandAdmin
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    console.log('🛡️ ProtectedRoute: No user, redirecting to /signin');
    return <Navigate to="/signin" replace />;
  }

  // Check role requirements
  const hasRequiredSuperAdmin = !requireSuperAdmin || canAccessSuperAdmin();
  const hasRequiredBrandAdmin = !requireBrandAdmin || canAccessBrandAdmin();
  
  console.log('🛡️ ProtectedRoute: Access checks:', {
    hasRequiredSuperAdmin,
    hasRequiredBrandAdmin,
    canAccessSuperAdmin: canAccessSuperAdmin(),
    canAccessBrandAdmin: canAccessBrandAdmin()
  });
  
  // In development mode, allow access to admin routes even without proper role
  if (import.meta.env.DEV && (requireSuperAdmin || requireBrandAdmin)) {
    const lacksSuperAdmin = requireSuperAdmin && !canAccessSuperAdmin();
    const lacksBrandAdmin = requireBrandAdmin && !canAccessBrandAdmin();
    
    // Only show warning if user actually lacks the required role
    if (lacksSuperAdmin || lacksBrandAdmin) {
      return (
        <div>
          <div className="bg-yellow-100 border-l-4 border-yellow-500 p-2 mb-4">
            <p className="text-yellow-700 text-sm">
              ⚠️ Dev Mode: Accessing admin route without proper role ({profile?.role || 'none'})
            </p>
          </div>
          {children}
        </div>
      );
    }
  }

  if (requireSuperAdmin && !canAccessSuperAdmin()) {
    console.log('🛡️ ProtectedRoute: Super admin required but not authorized, redirecting to /signin');
    return <Navigate to="/signin" replace />;
  }

  if (requireBrandAdmin && !canAccessBrandAdmin()) {
    console.log('🛡️ ProtectedRoute: Brand admin required but not authorized, redirecting to /signin');
    return <Navigate to="/signin" replace />;
  }

  console.log('🛡️ ProtectedRoute: Access granted!');
  return <>{children}</>;
};

export default ProtectedRoute;